import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import TextField from 'modules/core/components/TextField'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { Country } from 'modules/legal_entities/lib/constants'
import idValidationStrategies from 'modules/legal_entities/lib/identifierValidators'
import getMaxLengthByCountryAndIdType from 'modules/legal_entities/lib/identifierValidators/fieldLength'
import { GermanyIdType } from 'modules/legal_entities/lib/identifierValidators/germany/idType'
import getPlaceholderByCountryAndIdType from 'modules/legal_entities/lib/identifierValidators/placeholders'
import useIdentifierForm from 'modules/payroll_employees/hooks/useIdentifierForm'
import { SupportedCountry } from 'modules/payroll_employees/lib/enums'

const COUNTRY = SupportedCountry.Germany
const SSN_LABEL = 'Sozialversicherungsnummer'
const TAX_ID_LABEL = 'Steuer-ID'

interface Props {
  employeeId: string
  mandatory: boolean
  readonly: boolean
  setSubmitEventHandler: (eventHandler: () => void) => void
  setIdentifiersDirty: (dirty: boolean) => void
  setIdentifiersError?: (error: boolean) => void
}

const Germany = ({
  employeeId,
  mandatory,
  readonly,
  setSubmitEventHandler,
  setIdentifiersDirty,
  setIdentifiersError,
}: Props) => {
  const form = useIdentifierForm({
    employeeId: employeeId,
    country: COUNTRY,
    setSubmitEventHandler,
  })

  const [ssn, setSsn] = React.useState(form.get('social_security_number').value)
  const [taxId, setTaxId] = React.useState(form.get('tax_id').value)
  useUnsafeEffect(() => {
    setTaxId(form.get('tax_id').value)
    setSsn(form.get('social_security_number').value)
  }, [form])
  const handleSsnChange = (x: string) => {
    setSsn(x)
    form.setValues({ social_security_number: x })
    setIdentifiersDirty(form.isDirty)
  }
  const handleTaxIdChange = (x: string) => {
    setTaxId(x)
    form.setValues({ ...form.values, tax_id: x })
    setIdentifiersDirty(form.isDirty)
  }

  const ssnError = form.get('social_security_number').isDirty
    ? idValidationStrategies(
        Country.Germany as Country,
        'social_security_number' as GermanyIdType,
        form.get('social_security_number').value,
        SSN_LABEL
      ).errors()
    : ''

  const taxIdError = form.get('tax_id').isDirty
    ? idValidationStrategies(
        Country.Germany as Country,
        'steuer_id' as GermanyIdType,
        form.get('tax_id').value,
        TAX_ID_LABEL
      ).errors()
    : ''

  useEffect(() => {
    if (setIdentifiersError) setIdentifiersError(!!ssnError || !!taxIdError)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssnError, taxIdError])

  return (
    <Box paddingTop="s24" gap="s24" width="full">
      <TextField
        mandatory={mandatory}
        readonly={readonly}
        label={SSN_LABEL}
        value={ssn}
        onChange={handleSsnChange}
        placeholder={getPlaceholderByCountryAndIdType(
          Country.Germany as Country,
          'social_security_number' as GermanyIdType
        )}
        error={ssnError}
        maxLength={getMaxLengthByCountryAndIdType(
          Country.Germany as Country,
          'social_security_number' as GermanyIdType
        )}
      />
      <TextField
        mandatory={mandatory}
        readonly={readonly}
        label={TAX_ID_LABEL}
        value={taxId}
        onChange={handleTaxIdChange}
        placeholder={getPlaceholderByCountryAndIdType(
          Country.Germany as Country,
          'steuer_id' as GermanyIdType
        )}
        error={taxIdError}
        maxLength={getMaxLengthByCountryAndIdType(
          Country.Germany as Country,
          'steuer_id' as GermanyIdType
        )}
      />
    </Box>
  )
}

export default React.memo(Germany)
